import { useState, useEffect, useCallback } from 'react';
import { ENDPOINTS } from '../api/api';

const CARRIER_COLORS = { mtn: '#F5C518', vodacom: '#E62801' };

function carrierColor(raw = '') {
  const n = raw.toLowerCase();
  if (n.includes('mtn')) return CARRIER_COLORS.mtn;
  if (n.includes('vodacom')) return CARRIER_COLORS.vodacom;
  return '#505050';
}

function metaValue(metaData = [], ...keys) {
  for (const key of keys) {
    const entry = metaData.find((m) => {
      const k = (m.metadataKey || m.key || m.attributeKey || m.name || '').toLowerCase();
      return k === key.toLowerCase();
    });
    if (entry) return entry.metadataValue || entry.value || entry.attributeValue || entry.val || null;
  }
  return null;
}

function bytesToMb(bytes) {
  if (!bytes) return '0 MB';
  return `${(bytes / 1048576).toFixed(0)} MB`;
}

export function useDeviceData(imei) {
  const [state, setState] = useState({ loading: true, error: null, device: null });
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!imei) return;
    setState({ loading: true, error: null, device: null });

    fetch(ENDPOINTS.device(imei))
      .then((r) => r.json())
      .then((json) => {
        const d = json.device;
        if (!d) throw new Error('Device not found');

        const meta    = Array.isArray(d.metaData) ? d.metaData : [];
        const storage = d.internalStorage || {};
        const battery = d.battery         || {};
        const usage   = d.dataUsage       || {};
        const hw      = d.hardware        || {};
        const conn    = d.connectivity    || {};
        const carrier = d.networkInfo?.currentCarrier || '';

        setState({
          loading: false,
          error: null,
          device: {
            imeiNumber:   d.imeiNumber,
            serialNumber: d.serialNumber,
            model:        hw.model        || d.model,
            manufacturer: hw.manufacturer || d.manufacturer,
            osVersion:    hw.operatingSystem ? `Android ${hw.operatingSystem}` : '—',
            cpuSpeed:     hw.cpuSpeed ? `${hw.cpuSpeed} GHz` : '—',
            lastReportedTime: d.lastReportedTime,

            learnerName: d.name || metaValue(meta, 'name', 'learner_name') || '—',
            grade:       metaValue(meta, 'grade', 'learner_grade') || '—',
            gender:      metaValue(meta, 'gender') || '—',
            school:      d.school   || '—',
            district:    d.region   || '—',
            circuit:     d.province || '—',
            branch:      d.branch   || '',
            emiscode:    d.emiscode ?? null,

            battery:       battery.level  || 0,
            batteryStatus: battery.status || 'Unknown',

            storageUsed:  parseFloat(((storage.used || 0) / 1073741824).toFixed(1)),
            storageTotal: parseFloat(((storage.size || (storage.used || 0) + (storage.free || 0) || 1) / 1073741824).toFixed(1)),
            storageFree:  parseFloat(((storage.free || 0) / 1073741824).toFixed(1)),

            carrier:      carrier || 'Unknown',
            carrierColor: carrierColor(carrier),

            mobileData:   bytesToMb((usage.rxBytesMobile || 0) + (usage.txBytesMobile || 0)),
            mobileDataRx: bytesToMb(usage.rxBytesMobile || 0),
            mobileDataTx: bytesToMb(usage.txBytesMobile || 0),
            wifiData:     bytesToMb((usage.rxBytes || 0) + (usage.txBytes || 0)),
            wifiDataRx:   bytesToMb(usage.rxBytes || 0),
            wifiDataTx:   bytesToMb(usage.txBytes || 0),

            dualSim:      conn.dualSim,
            wifiEnabled:  conn.wifiEnabled,
            btEnabled:    conn.btEnabled,
            gpsEnabled:   conn.gpsEnabled,
            wifiSSID:     conn.wifiSSID,
            ipAddress:    conn.ipAddress,

            coords: d.realTimeStats?.currentLatitude && d.realTimeStats?.currentLongitude
              ? [d.realTimeStats.currentLatitude, d.realTimeStats.currentLongitude]
              : [-23.9045, 29.4686],

            appUsageData:  Array.isArray(d.appUsageData) ? d.appUsageData : [],
            installedApps: Array.isArray(d.apps) ? d.apps : [],
          },
        });
      })
      .catch((err) => setState({ loading: false, error: err.message, device: null }));
  }, [imei, refreshKey]);

  return { ...state, refresh };
}
